import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import {
  LayoutDashboard,
  Users,
  History,
  MoreHorizontal,
  UserSearch,
  Palette,
  UsersRound,
  Building2,
  UserCog,
  ListTodo,
  X,
} from "lucide-react";
import { useAuthContext } from "@/components/auth/useAuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  StyledBottomNavigation,
  StyledBottomNavigationAction,
  BOTTOM_NAV_HEIGHT,
} from "./BottomNavigation.styles";

/** Primary navigation items shown in bottom bar */
const primaryNavItems = [
  { value: "dashboard", label: "Home", icon: LayoutDashboard, href: "/" },
  { value: "contacts", label: "Contacts", icon: Users, href: "/contacts" },
  {
    value: "history",
    label: "History",
    icon: History,
    href: "/reports/history",
  },
  { value: "more", label: "More", icon: MoreHorizontal, href: null },
];

/** Secondary navigation items shown in "More" drawer */
const secondaryNavItems = [
  {
    value: "prospects",
    label: "Prospects",
    icon: UserSearch,
    href: "/prospects",
  },
  { value: "templates", label: "Templates", icon: Palette, href: "/templates" },
];

/** Org admin items (visible to org admins and super_admins) */
const orgAdminNavItems = [
  { value: "team", label: "Team", icon: UsersRound, href: "/team" },
];

/** Super admin items (visible to super_admins only) */
const superAdminNavItems = [
  { value: "tenants", label: "Tenants", icon: Building2, href: "/tenants" },
  { value: "users", label: "Users", icon: UserCog, href: "/users" },
  { value: "jobs", label: "Jobs", icon: ListTodo, href: "/jobs" },
  {
    value: "approvals",
    label: "Approvals",
    icon: ListTodo,
    href: "/approvals",
  },
];

/**
 * Get the active navigation value based on current path
 */
function getActiveValue(pathname: string): string {
  if (pathname === "/") return "dashboard";
  if (pathname.startsWith("/contacts")) return "contacts";
  if (pathname.startsWith("/reports/history")) return "history";
  if (pathname.startsWith("/prospects")) return "more";
  if (pathname.startsWith("/templates")) return "more";
  if (pathname.startsWith("/team")) return "more";
  if (pathname.startsWith("/tenants")) return "more";
  if (pathname.startsWith("/users")) return "more";
  if (pathname.startsWith("/jobs")) return "more";
  if (pathname.startsWith("/approvals")) return "more";
  return "dashboard";
}

export { BOTTOM_NAV_HEIGHT };

/**
 * Mobile bottom navigation bar.
 * Only renders on mobile viewports (< 900px).
 * Provides quick access to primary destinations and a "More" drawer for secondary items.
 */
export function BottomNavigation() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useAuthContext();
  const [moreOpen, setMoreOpen] = useState(false);

  // Don't render on desktop
  if (!isMobile) return null;

  const activeValue = getActiveValue(location.pathname);

  const handleNavChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (newValue === "more") {
      setMoreOpen(true);
    } else {
      const item = primaryNavItems.find((i) => i.value === newValue);
      if (item?.href) {
        navigate(item.href);
      }
    }
  };

  const handleMoreItemClick = (href: string) => {
    setMoreOpen(false);
    navigate(href);
  };

  return (
    <>
      <StyledBottomNavigation
        value={activeValue}
        onChange={handleNavChange}
        showLabels
        aria-label="Main navigation"
      >
        {primaryNavItems.map((item) => (
          <StyledBottomNavigationAction
            key={item.value}
            value={item.value}
            label={item.label}
            icon={<item.icon size={24} />}
            aria-current={activeValue === item.value ? "page" : undefined}
          />
        ))}
      </StyledBottomNavigation>

      {/* More drawer */}
      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "70vh",
            pb: "env(safe-area-inset-bottom, 0px)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              More
            </Typography>
            <Box
              component="button"
              onClick={() => setMoreOpen(false)}
              sx={{
                p: 1,
                border: "none",
                background: "none",
                cursor: "pointer",
                borderRadius: 1,
                display: "flex",
                "&:hover": { bgcolor: "action.hover" },
              }}
              aria-label="Close menu"
            >
              <X size={24} />
            </Box>
          </Box>

          <List disablePadding>
            {secondaryNavItems.map((item) => (
              <ListItemButton
                key={item.value}
                onClick={() => handleMoreItemClick(item.href)}
                selected={location.pathname.startsWith(item.href)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <item.icon size={20} />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}

            {isAdmin && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2, py: 0.5, display: "block" }}
                >
                  Team Management
                </Typography>
                {orgAdminNavItems.map((item) => (
                  <ListItemButton
                    key={item.value}
                    onClick={() => handleMoreItemClick(item.href)}
                    selected={location.pathname.startsWith(item.href)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <item.icon size={20} />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </>
            )}

            {isSuperAdmin && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2, py: 0.5, display: "block" }}
                >
                  System Admin
                </Typography>
                {superAdminNavItems.map((item) => (
                  <ListItemButton
                    key={item.value}
                    onClick={() => handleMoreItemClick(item.href)}
                    selected={location.pathname.startsWith(item.href)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <item.icon size={20} />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
